import React, { FC, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { FormInstance } from "antd";
import Form from "antd/es/form/Form";
import cn from "classnames";

import User from "@mapmycustomers/shared/types/User";

import CardType from "@app/enum/dashboard/CardType";
import getGoalConfigurationProperties from "@app/scene/dashboard/utils/getGoalConfigurationProperties";
import { getCurrentUser } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import CardConfiguration, { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";

import Preview from "../Preview";

import Configuration from "./components/Configuration";
import Details from "./components/Details";
import styles from "./EditCardForm.module.scss";

export interface FormValue extends CardConfiguration<any, any, any> {
  individualGoal?: number;
}

export interface EditCardProps {
  className?: string;
  configuration: CardConfiguration<any, any, any>;
  details?: Pick<DashboardCard, "createdAt" | "createdBy" | "updatedAt" | "updatedBy">;
  form: FormInstance<CardConfiguration<any, any, any>>;
  onChange?: (currentConfiguration: FormValue) => void;
  onSubmit?: (currentConfiguration: FormValue) => void;
  scope: Scope;
  teamGoal?: GoalConfiguration;
  type: CardType;
}

interface Props extends EditCardProps {
  currentUser: User;
  users: User[];
}

const EditCardForm: FC<Props> = ({
  className,
  configuration,
  currentUser,
  details,
  form,
  onChange,
  onSubmit,
  scope,
  teamGoal,
  type,
  users,
}) => {
  const [currentConfiguration, setCurrentConfiguration] = useState<FormValue>(configuration);

  const handleFormChange = useCallback(() => {
    const currentConfiguration = form.getFieldsValue();
    setCurrentConfiguration(currentConfiguration);
    onChange?.(currentConfiguration);
  }, [form, onChange]);

  const handleFormSubmit = useCallback(() => {
    onSubmit?.(form.getFieldsValue());
  }, [form, onSubmit]);

  const goalProperties = getGoalConfigurationProperties(
    currentUser,
    scope,
    users,
    configuration.goal,
    teamGoal
  );

  const initialValues = useMemo(
    () => {
      const result: FormValue = { ...configuration };
      if (goalProperties.individualGoal) {
        result.individualGoal = goalProperties.value;
      }
      if (teamGoal?.enabled) {
        // take "enabled" flag from the team goal if set
        result.goal = { ...(result.goal ?? {}), enabled: true };
      }
      return result;
    },
    // We don't need goal value to change initialValues
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goalProperties.individualGoal, configuration, teamGoal?.enabled]
  );

  const previewConfiguration = useMemo(() => {
    let previewConfiguration = currentConfiguration;
    if (
      currentConfiguration.goal &&
      currentConfiguration.individualGoal !== undefined &&
      scope.userId
    ) {
      previewConfiguration = {
        ...currentConfiguration,
        goal: {
          ...currentConfiguration.goal!,
          ...configuration.goal,
          individualUserGoals: {
            ...(currentConfiguration.goal.individualUserGoals ?? {}),
            [scope.userId!]: currentConfiguration.individualGoal,
          },
        },
      };
    }

    if (currentConfiguration.goal?.enabled && !teamGoal?.enabled) {
      previewConfiguration = {
        ...currentConfiguration,
        goal: {
          ...currentConfiguration.goal!,
          personal: true,
        },
      };
    }

    return previewConfiguration;
  }, [currentConfiguration, configuration.goal, scope.userId, teamGoal?.enabled]);

  return (
    <Form
      form={form}
      initialValues={initialValues}
      layout="vertical"
      onFieldsChange={handleFormChange}
      onFinish={handleFormSubmit}
      preserve
    >
      <div className={cn(styles.container, className)}>
        <div className={styles.previewSide}>
          <Preview configuration={previewConfiguration} scope={scope} type={type} />
          <Details details={details} />
        </div>
        <div className={styles.configSide}>
          <Configuration
            goalConfiguration={configuration.goal} // we do not need current configuration here
            scope={scope}
            teamGoal={teamGoal}
            type={type}
          />
        </div>
      </div>
    </Form>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
  users: getUsers(state),
});

export default connect(mapStateToProps)(EditCardForm);
